import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BadgeCheck, Plus, Search } from 'lucide-react';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import ErroreLettura from '../components/comuni/ErroreLettura';
import { albiAPI } from '../services/api';
import { conUnTentativo, motivoDellaLettura } from '../utils/primaLettura';
import '../styles/AlbiSedi.css';

/**
 * AlbiSedi — le liste chiuse dell'iscrizione all'albo.
 *
 * Gli albi sono fissi (migration 190). Le SEDI sono quello che
 * l'amministrazione puo' aggiungere: le 107 province dell'Ordine dei
 * Medici e le regioni degli Psicologi ci sono tutte; Biologi,
 * TSRM-PSTRP e Fisioterapisti hanno sedi territoriali che cambiano per
 * legge, e quando arriva un professionista iscritto in una sede che
 * manca la si aggiunge da qui - con il nome e la forma per la stampa
 * («di Rimini», «dell'Emilia-Romagna»). Chi compila la scheda del
 * medico continua a scegliere da una tendina.
 *
 * LA RICERCA (Alex, 25 settembre 2026). Sono centinaia di sedi: una
 * casella sopra l'elenco filtra mentre si scrive - nome della sede,
 * codice, forma per la stampa, nome dell'albo - cosi' nessuno deve
 * scorrere tutto. I nomi lunghi restano troncati: e' una scelta.
 */
const normalizza = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const VUOTA = { codice: '', nome: '', stampa: '' };

const AlbiSedi = () => {
    const [albi, setAlbi] = useState([]);
    const [aperto, setAperto] = useState(null);      // codice dell'albo con il modulo aperto
    const [nuova, setNuova] = useState(VUOTA);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [cerca, setCerca] = useState('');
    /* L'errore della lettura sta per conto suo: la lettura riuscita lo
       toglie, quello di un salvataggio resta (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);

    const carica = useCallback(async () => {
        try {
            const r = await conUnTentativo(() => albiAPI.elenco());
            setAlbi(r.data?.data?.albi || []);
            setErroreLettura(null);
        } catch (e) { setErroreLettura(motivoDellaLettura(e, 'Non riesco a leggere gli albi')); }
    }, []);
    useEffect(() => { carica(); }, [carica]);

    const salva = async (codice) => {
        setErrore(null);
        try {
            await albiAPI.aggiungiSede(codice, nuova);
            setAvviso(`Sede «${nuova.nome}» aggiunta.`);
            setNuova(VUOTA);
            setAperto(null);
            carica();
        } catch (e) { setErrore(e?.response?.data?.message || 'Errore nel salvataggio della sede'); }
    };

    /* Un albo il cui nome corrisponde mostra tutte le sue sedi; altrimenti
       solo le sedi che corrispondono, e l'albo sparisce se non ne resta
       nessuna. */
    const visibili = useMemo(() => {
        const q = normalizza(cerca).trim();
        if (!q) return albi;
        return albi.map((a) => {
            if (normalizza(a.nome).includes(q)) return a;
            const sedi = a.sedi.filter((x) => [x.nome, x.codice, x.stampa].some((v) => normalizza(v).includes(q)));
            return { ...a, sedi };
        }).filter((a) => a.sedi.length > 0);
    }, [albi, cerca]);
    const quante = visibili.reduce((t, a) => t + a.sedi.length, 0);

    const attiva = async (sede, v) => {
        try { await albiAPI.aggiornaSede(sede.id, { attiva: v }); carica(); }
        catch (e) { setErrore(e?.response?.data?.message || 'Errore'); }
    };

    return (
        <div className="as-pagina">
            <IntestazioneSezione
                etichetta="Amministrazione"
                titolo="Albi e sedi"
                icona={<BadgeCheck size={22} aria-hidden="true" />}
                sottotitolo="Le liste da cui si sceglie l'iscrizione all'albo di un professionista. Gli albi sono fissi; le sedi si aggiungono quando servono."
            />
            <ErroreLettura testo={erroreLettura} onRiprova={carica} />
            {errore && <p className="rm-messaggio rm-messaggio-errore">{errore}</p>}
            {avviso && <p className="rm-messaggio rm-messaggio-conferma">{avviso}</p>}

            <div className="as-cerca">
                <Search size={16} aria-hidden="true" />
                <input type="search" value={cerca} onChange={(e) => setCerca(e.target.value)}
                       placeholder="Cerca una sede o un albo…" aria-label="Cerca una sede o un albo" />
                {cerca && <span className="as-conteggio">{quante === 1 ? '1 sede' : `${quante} sedi`}</span>}
            </div>
            {cerca && visibili.length === 0 && <p className="as-vuoto">Nessuna sede corrisponde alla ricerca.</p>}

            {visibili.map((a) => (
                <section key={a.codice} className="as-albo">
                    <h2>{a.nome} <span className="as-ambito">{{ provincia: 'per provincia', regione: 'per regione', sede: 'per sede territoriale' }[a.ambito]}</span></h2>
                    <ul className="as-sedi">
                        {a.sedi.map((s) => (
                            <li key={s.id} className={s.attiva === false ? 'as-spenta' : ''}>
                                <span className="as-nome">{s.nome}</span>
                                <span className="as-stampa">«Iscritto all'{a.nome} {s.stampa} n. …»</span>
                                <button type="button" className="as-spegni" onClick={() => attiva(s, s.attiva === false)}>
                                    {s.attiva === false ? 'riattiva' : 'nascondi'}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {aperto === a.codice ? (
                        <div className="as-nuova">
                            <label><span>Codice</span>
                                <input value={nuova.codice} maxLength={20} placeholder="Es. FC-RN"
                                       onChange={(e) => setNuova({ ...nuova, codice: e.target.value })} /></label>
                            <label><span>Nome</span>
                                <input value={nuova.nome} maxLength={120} placeholder="Es. Forlì-Cesena e Rimini"
                                       onChange={(e) => setNuova({ ...nuova, nome: e.target.value })} /></label>
                            <label><span>Per la stampa</span>
                                <input value={nuova.stampa} maxLength={160} placeholder="Es. delle province di Forlì-Cesena e Rimini"
                                       onChange={(e) => setNuova({ ...nuova, stampa: e.target.value })} /></label>
                            <button type="button" className="rm-btn rm-btn-primario" onClick={() => salva(a.codice)}>Aggiungi</button>
                            <button type="button" className="rm-btn" onClick={() => { setAperto(null); setNuova(VUOTA); }}>Annulla</button>
                        </div>
                    ) : (
                        <button type="button" className="rm-btn as-aggiungi" onClick={() => setAperto(a.codice)}>
                            <Plus size={14} /> Aggiungi una sede
                        </button>
                    )}
                </section>
            ))}
        </div>
    );
};

export default AlbiSedi;
